import { Box, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import {
  DossierBudgetTransactionFilters,
  getDossierBudgetTransactions,
} from "../../../api/dossier/getDossierBudgetTransactions";
import APFilterableList from "../../../components/APFilterableList";
import DossierCategoryTotalRow from "../../../components/charts/APCharts/DossierCategoryTotalRow";
import DossierSelector from "../../../components/dossier/DossierSelector";
import APAutoComplete, {
  AutoCompleteOption,
} from "../../../components/form/APAutoComplete";
import { isDossierActive } from "../../../helpers/dossiers";
import { useDossierActivationCheck } from "../../../hooks/useDossierActivationCheck";
import { useAuth } from "../../../providers/AuthProvider";
import {
  DossierTransaction,
  DossierTransactionSpentType,
  DossierTransactionType,
  DossierTransactionTypeLabelMap,
  DossierTransactionTypeOptions,
} from "../../../types/DossierTransaction";
import ActiveDossierNavButtons from "../components/ActiveDossierNavButtons";
import DossierBalances from "../components/DossierBalances";
import DossierTransactionCard from "../components/DossierTransactionCard";

type DossierBudgetFilterInputs = {
  category: AutoCompleteOption | null;
  includes: AutoCompleteOption[] | null;
};

const includesOptions: AutoCompleteOption[] = [
  { id: "invoiced", label: "Invoiced" },
  { id: "spent", label: "Spent" },
];

export const DEFAULT_PAGE_SIZE = 15;

const ViewDossierBudget = () => {
  const { dossierId, budgetId } = useParams<{
    dossierId: string;
    budgetId: string;
  }>();
  const [searchParams] = useSearchParams();
  const includes = searchParams.get("includes")?.split(",");
  const includeInvoiced = !includes || includes.includes("invoiced");
  const includeSpent = !includes || includes.includes("spent");

  const navigate = useNavigate();
  const { getScopedPageUrl } = useAuth();
  const [filters, setFilters] = useState<DossierBudgetTransactionFilters>({
    dossierId: dossierId || "",
    pageSize: DEFAULT_PAGE_SIZE,
    transactionType: budgetId as DossierTransactionType,
    includeInvoiced: includeInvoiced,
    includeSpent: includeSpent,
  });

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<DossierBudgetFilterInputs>({
    mode: "onTouched",
  });

  const { navigationCheckComplete, isLoading, isSuccess, activatedDossiers } =
    useDossierActivationCheck({
      dossierId,
    });

  const selectedDossier = activatedDossiers.find(
    (dossier) => dossier.id === dossierId
  );

  useEffect(() => {
    if (dossierId && dossierId !== filters.dossierId) {
      setFilters({ ...filters, dossierId, nextPageLink: undefined });
    }
  }, [dossierId]);

  const inputsToFilters = (inputs: DossierBudgetFilterInputs) => {
    return {
      ...filters,
      transactionType: inputs.category?.id as DossierTransactionType,
      includeInvoiced:
        inputs.includes?.some((include) => include.id === "invoiced") ?? false,
      includeSpent:
        inputs.includes?.some((include) => include.id === "spent") ?? false,
    };
  };

  const onFilterSubmit = (inputs: DossierBudgetFilterInputs) => {
    const queryString = inputs.includes
      ? `?includes=${inputs.includes.map((include) => include.id).join(",")}`
      : "";

    if (inputs.category) {
      navigate(
        `${getScopedPageUrl(
          "dossierBudget",
          dossierId,
          inputs.category.id as DossierTransactionType
        )}${queryString}`
      );
    }
  };

  const renderDossierTransactionCard = (transaction: DossierTransaction) => {
    return transaction.incGst === 0 ? (
      <></>
    ) : (
      <DossierTransactionCard key={transaction.id} transaction={transaction} />
    );
  };

  const categoryBalance =
    selectedDossier?.categoryBalances?.[
      budgetId as DossierTransactionSpentType
    ];

  let selectedBalance;

  if (categoryBalance) {
    if (includeInvoiced && includeSpent) {
      selectedBalance = categoryBalance.balance;
    } else if (includeInvoiced) {
      selectedBalance = categoryBalance.budget;
    } else if (includeSpent) {
      selectedBalance = categoryBalance.expenses;
    }
  }

  return (
    <APFilterableList
      key={dossierId}
      title={`${
        DossierTransactionTypeLabelMap[budgetId as DossierTransactionType]
      } Budget`}
      filters={filters}
      setFilters={setFilters}
      pageSize={DEFAULT_PAGE_SIZE}
      getItems={getDossierBudgetTransactions}
      inputsToFilters={inputsToFilters}
      handleSubmit={handleSubmit}
      errors={errors}
      formFields={
        <>
          <APAutoComplete
            name="category"
            label="Category"
            options={DossierTransactionTypeOptions.filter((option) =>
              [
                "fuel",
                "servicing",
                "tyres",
                "rego",
                "insurance",
                "finance",
                "fees",
              ].includes(option.id as DossierTransactionType)
            )}
            control={control}
            validations={{ required: true }}
            errors={errors}
            defaultValue={DossierTransactionTypeOptions.find(
              (option) => option.id === budgetId
            )}
            setValue={setValue}
          />
          <APAutoComplete
            name="includes"
            label="Includes"
            options={includesOptions}
            control={control}
            validations={{ required: true }}
            defaultValue={[
              ...(includeInvoiced
                ? [{ id: "invoiced", label: "Invoiced" }]
                : []),
              ...(includeSpent ? [{ id: "spent", label: "Spent" }] : []),
            ]}
            errors={errors}
            setValue={setValue}
            multiple
          />
        </>
      }
      renderItems={(items) => items.map(renderDossierTransactionCard)}
      isLoading={isLoading || !navigationCheckComplete}
      isSuccess={isSuccess && navigationCheckComplete && !!dossierId}
      topCollapseContent={
        selectedDossier && (
          <Box mb={2}>
            <DossierSelector
              dossiers={activatedDossiers}
              selectedDossier={selectedDossier}
              redirectTo="dossierTransactions"
              showDivider
            />
          </Box>
        )
      }
      abovePaginationContent={
        <Box mr={2}>
          {categoryBalance && (
            <Stack spacing={1} pt={1} pb={2}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  borderBottom: "1px solid",
                  borderColor: "outlineVariant.main",
                  py: 0.5,
                }}
              >
                <Box sx={{ flex: 1 }} />
                <Box sx={{ flex: 1, display: "flex", justifyContent: "end" }}>
                  <Typography variant="titleSmall">Invoiced</Typography>
                </Box>
                <Box sx={{ flex: 1, display: "flex", justifyContent: "end" }}>
                  <Typography variant="titleSmall">Spent</Typography>
                </Box>
                <Box sx={{ flex: 1, display: "flex", justifyContent: "end" }}>
                  <Typography variant="titleSmall">Balance</Typography>
                </Box>
              </Box>
              <DossierCategoryTotalRow
                label={
                  DossierTransactionTypeLabelMap[
                    budgetId as DossierTransactionType
                  ]
                }
                values={[
                  categoryBalance.budget,
                  categoryBalance.expenses,
                  categoryBalance.balance,
                ]}
                isTotal={false}
                textSize="small"
                decimalPlaces={2}
              />
            </Stack>
          )}
          <Box sx={{ display: "flex", justifyContent: "flex-end" }} mt={1}>
            <Box>
              <Typography variant="bodySmall">Ex GST</Typography>
            </Box>
          </Box>
        </Box>
      }
      overrideContent={
        !selectedDossier && (
          <Typography variant="bodyMedium">Dossier is not activated</Typography>
        )
      }
      onFilterSubmit={onFilterSubmit}
      bottomContent={
        <Stack spacing={3} pt={2}>
          {categoryBalance && (
            <Box>
              <Box mr={2}>
                <DossierBalances totalBalance={selectedBalance} />
              </Box>
            </Box>
          )}
          {selectedDossier && (
            <ActiveDossierNavButtons
              dossier={selectedDossier}
              backTypes={["back", "home"]}
              navButtons={["view-budgets", "view-balances"]}
              rightNavButtons={
                isDossierActive(selectedDossier) ? ["submit-claim"] : undefined
              }
            />
          )}
        </Stack>
      }
    />
  );
};

export default ViewDossierBudget;
